import { IDesignerState } from '@/../typings';
import { filestore, FsImage } from '@/filestore';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DoubleSide, FrontSide } from 'three';
import SelectedEntityPositionHotspot from '../Hotspots/SelectedEntityPositionHotspot';
import LineBox from '../LineBox/LineBox';
import { IImage, IImageReactProps, ITuple3 } from '../r3f-components/component-data-structure';
import CustomBillboard from '../r3f-components/components/CustomBillboard/CustomBillboard';
import EntityBoundaries from './EntityBoundaries/EntityBoundaries';
import { useGetHocBorderProperties, useGetHocCurvedProperties, useGetHocSpatialProperties, useHocEntityPointerMove, useHocPointerDown, useHocPointerMove, useHocPointerOut, useHocPointerOver, useHocPointerUp, useShowHighlight } from './hocCustomHooks';

interface IImageWrapped {
	id: string;
	enabled?: boolean;
}

const withImageBehaviour = (WrappedImage: FunctionComponent<IImageReactProps>): FunctionComponent<IImageWrapped> => {
	const FuncComp: FunctionComponent<IImageWrapped> = ({ enabled: entityIsEnabled = true, id }) => {
		// Selectors
		const entity = useSelector((state: IDesignerState) => state.contentReducer.contentDoc.componentsById[id] as IImage);
		const projectShadowsEnabled = useSelector((state: IDesignerState) => state.contentReducer.contentDoc.shadowsEnabled ?? true);

		// Refs
		const pointerStageRef = useRef<'down' | 'move' | 'up' | null>(null);
		const shiftLockedRef = useRef<number | null>(null);
		const initialPosRef = useRef<ITuple3 | null>(null); // Used to calculate pointer offset in pointerMove
		const previousPositionRef = useRef<ITuple3 | null>(null); // The previous position, used to compare with the current position
		const pointerUpHasRunRef = useRef<boolean | null>(null); // To prevent multiple dispatched actions when pointerUp runs twice this is reset onPointerMove
		const [showEntityPositionHotspot, setShowEntityPositionHotspot] = useState(false);

		// Entity properties to pass to wrapped component
		const { opacity, hasAlpha, castShadow = false, receiveShadow = false, depthTest = true, billboard, renderOrder } = entity;
		const { scale, position, rotation } = useGetHocSpatialProperties(entity);
		const { borderWidth, borderRadius, borderRgba } = useGetHocBorderProperties(entity);
		const { curvature } = useGetHocCurvedProperties(entity);
        const [imageUrl, setImageUrl] = useState<string | undefined>()
        const [imageHasError, setImageHasError] = useState(false);
		const [isHovered, setIsHovered] = useState(false);

		// Pointer events
		const onPointerDown = useHocPointerDown({ pointerStageRef, initialPosRef, entity, setShowEntityPositionHotspot });
		const onPointerMove = useHocPointerMove({ pointerStageRef, initialPosRef, pointerUpHasRunRef, previousPositionRef, entity, shiftLockedRef });
		const onPointerUp = useHocPointerUp({ pointerStageRef, pointerUpHasRunRef, initialPosRef, entity, setShowEntityPositionHotspot, shiftLockedRef });
        
        const onPointerOver = useHocPointerOver(entity, () => setIsHovered(true));
        const onPointerOut = useHocPointerOut(entity, () => setIsHovered(false));
        const onEntityPointerMove = useHocEntityPointerMove(entity, () => setIsHovered(false), () => setIsHovered(true));
        const showHighlight = useShowHighlight(id, isHovered);

		useEffect(() => {
            try {
                const fsImage = filestore.load<FsImage>(entity.filestoreId)
                fsImage.getUrl()
                    .then(url => setImageUrl(url))
                    .catch(() => setImageHasError(true))
            } catch {
                setImageHasError(true);
            }
			
		}, [entity.filestoreId])

		return (
			<>
				<EntityBoundaries
                    forceError={imageHasError}
                    forceLoading={typeof imageUrl === 'undefined'}
					position={position}
					rotation={rotation}
					scale={scale}
					renderOrder={renderOrder}
					onPointerDown={entityIsEnabled ? onPointerDown : undefined}
					onPointerUp={onPointerUp}
					label={`Image component ${id} render order ${renderOrder}`}
				>
                    <>
                        <WrappedImage
                            id={id}
                            scale={scale}
                            rotation={rotation}
                            position={position}
                            imageUrl={imageUrl!}
                            renderOrder={renderOrder}
                            opacity={opacity}
                            hasAlpha={hasAlpha}
                            borderWidth={borderWidth}
                            borderRgba={borderRgba}
                            borderRadius={borderRadius}
                            curvature={curvature}
                            castShadow={castShadow && projectShadowsEnabled}
                            side={entity.isSnappedToTarget ? FrontSide :  DoubleSide}
                            receiveShadow={receiveShadow && projectShadowsEnabled}
                            depthTest={depthTest}
                            billboard={billboard}
                            onPointerDown={entityIsEnabled ? onPointerDown : undefined}
                            onPointerUp={entityIsEnabled ? onPointerUp : undefined}
                            onPointerMove={entityIsEnabled ? onEntityPointerMove :  undefined}
                            onPointerOut={entityIsEnabled ? onPointerOut : undefined}
                            onPointerOver={entityIsEnabled ? onPointerOver : undefined}
                            isCameraOrbiting
                            rotateNoBillboardOuterGroup
                        />
                        <CustomBillboard 
                            position={position}
                            rotation={rotation}
                            follow={!!billboard?.[0]}
                            lockY={!billboard?.[1]}
                            visible={showHighlight}
                            isCameraOrbiting
                            rotateNoBillboardOuterGroup
                        >
                            <LineBox renderOrder={renderOrder} rotation={[0, 0 , 0]} scale={[scale[0] / 2, scale[1] / 2, scale[2] / 2]} color={[66, 135, 245, 1]} lineWidth={6} />
                        </CustomBillboard>
                    </>
				</EntityBoundaries>
				{showEntityPositionHotspot && <SelectedEntityPositionHotspot onPointerMove={showEntityPositionHotspot ? onPointerMove : undefined} onPointerUp={entityIsEnabled ? onPointerUp : undefined} />}
			</>
		);
	};
	return FuncComp;
};

export default withImageBehaviour;